import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { HiChevronDown, HiChevronRight } from "react-icons/hi";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { fetchAiConversations } from "../../redux/slices/aiConversations";

const AiConversations = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { data, pageLoading, error } = useSelector(
    (state) => state.aiConversations
  );
  const [expanded, setExpanded] = useState({});
  const [limit, setLimit] = useState(20);

  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAiConversations({ sampled: true, limit }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isAdmin]);

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  if (!isAdmin) {
    return (
      <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
        <NavBar />
        <SideNav />
        <main className="p-4 md:ml-64 h-auto pt-20">
          <Breadcrumb />
          <div className="mt-6 p-6 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
              AI Conversations
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Admin access required to review sampled advisor conversations.
            </p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              AI Conversations
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Sampled advisor threads with PII scrubbed for review.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm text-gray-600">
              Limit
              <input
                type="number"
                min={1}
                max={50}
                className="mt-1 block w-24 rounded-lg border-gray-300 text-sm"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value) || 20)}
              />
            </label>
            <Button
              color="purple"
              disabled={pageLoading}
              isProcessing={pageLoading}
              processingSpinner={
                <AiOutlineLoading className="h-5 w-5 animate-spin" />
              }
              onClick={() =>
                dispatch(fetchAiConversations({ sampled: true, limit }))
              }
            >
              Refresh
            </Button>
          </div>
        </section>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 border border-red-100 text-sm">
            {String(error)}
          </div>
        )}

        <div className="space-y-3">
          {pageLoading && data.length === 0 ? (
            <div className="py-16 text-center">
              <AiOutlineLoading className="inline h-6 w-6 animate-spin text-purple-600" />
            </div>
          ) : data.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 text-sm text-gray-500">
              No sampled conversations found.
            </div>
          ) : (
            data.map((conv) => {
              const open = Boolean(expanded[conv.id]);
              return (
                <div
                  key={conv.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden"
                >
                  <button
                    type="button"
                    className="w-full flex items-start gap-3 text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700"
                    onClick={() => toggle(conv.id)}
                  >
                    <span className="mt-1 text-gray-400">
                      {open ? (
                        <HiChevronDown className="h-5 w-5" />
                      ) : (
                        <HiChevronRight className="h-5 w-5" />
                      )}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 dark:text-white truncate">
                        {conv.title || "Untitled conversation"}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        uid{" "}
                        <code className="font-mono">{conv.uid || "—"}</code>
                        {" · "}
                        {conv.messages?.length || 0} messages
                        {" · "}
                        <code className="font-mono text-[10px]">{conv.id}</code>
                      </p>
                    </div>
                  </button>

                  {open && (
                    <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-3 space-y-3 bg-gray-50 dark:bg-gray-900/40 max-h-96 overflow-y-auto">
                      {(conv.messages || []).length === 0 ? (
                        <p className="text-sm text-gray-400 italic">
                          No messages.
                        </p>
                      ) : (
                        (conv.messages || []).map((msg) => (
                          <div
                            key={msg.id || `${msg.role}-${msg.content?.slice(0, 12)}`}
                            className={`text-sm max-w-[90%] px-3 py-2 rounded-lg ${
                              msg.role === "user"
                                ? "ml-auto bg-purple-600 text-white"
                                : "bg-white border border-gray-200 text-gray-800 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                            }`}
                          >
                            <p className="text-[10px] uppercase opacity-70 mb-1">
                              {msg.role}
                            </p>
                            <p className="whitespace-pre-wrap">{msg.content}</p>
                            {msg.citations?.length > 0 && (
                              <p className="mt-1 text-[11px] opacity-70">
                                citations: {msg.citations.join(", ")}
                              </p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
};

export default AiConversations;
